import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import Order, { OrderCanada } from "@repo/kikoff-models/shopping/Order";
import Product from "@repo/kikoff-models/shopping/Product";
import canadaRpc from "@repo/protobuf/api/canadaRpc";
import FeatureStore from "@repo/utils-solid/FeatureStore";

const initialState = {
  productById: {} as Record<Product.Id, Product>,
  allProducts: undefined as undefined | string[],
  orderById: {} as Record<Order.Id, Order>,
  allOrders: undefined as undefined | string[],
  selectedProduct: undefined as undefined | Product.Id,
};

export const { Provider, useShopping, Context } = FeatureStore.init(
  "Shopping",
  initialState,
  ([store, setStore], { attachAction, withLogger }) => {
    const accessors = {
      allProducts: attachAction(
        () => store.allProducts?.map((id) => store.productById[id]!) || [],
        () => actions.fetchProducts(),
        () => () => store.allProducts
      ),
      allOrders: attachAction(
        () => store.allOrders?.map((id) => store.orderById[id]!) || [],
        () => actions.fetchOrders(),
        () => () => store.allOrders
      ),
      selectedProduct: () =>
        store.selectedProduct && store.productById[store.selectedProduct],
    };

    const mutations = {
      selectProduct(productId: string | undefined) {
        setStore("selectedProduct", productId);
      },
      oneProduct(product: Product) {
        setStore("productById", product.id, reconcile(product));
      },
      manyProducts(products: Product[]) {
        batch(() => {
          for (const product of products) {
            mutations.oneProduct(product);
          }
        });
      },
      removeProduct(productId: string | undefined) {
        setStore(
          "allProducts",
          store.allProducts?.filter((id) => id !== productId)
        );
      },
      oneOrder(order: Order) {
        setStore("orderById", order.orderId, reconcile(order));
      },
      manyOrders(orders: Order[]) {
        batch(() => {
          for (const order of orders) {
            mutations.oneOrder(order);
          }
        });
      },
    };

    const us = {
      mutations: {},
      actions: {
        fetchProducts() {},
        fetchOrders() {},
        checkout() {},
      },
    };

    const canada = {
      actions: {
        fetchProducts() {
          return canadaRpc.Shopping.listProducts({}).then((data) => {
            mutations.manyProducts(data.products);
            setStore(
              "allProducts",
              data.products.map((product) => product.id)
            );
          });
        },
        fetchOrders() {
          return canadaRpc.Shopping.listOrders({}).then((data) => {
            mutations.manyOrders(data.orders.map(OrderCanada.normalize));
            setStore(
              "allOrders",
              data.orders.map((order) => order.orderId)
            );
          });
        },
        checkout(productId: Product.Id) {
          return canadaRpc.Shopping.checkout({ productId }).then((data) => {
            canada.actions.fetchOrders();
            mutations.removeProduct(data.order!.product!.id);
          });
        },
      },
    };

    const actions = withLogger("actions", {
      ...(import.meta.env.API_ADAPTER === "canada"
        ? canada.actions
        : us.actions),
      selectProduct: mutations.selectProduct,
    });

    return { accessors, actions };
  }
);
